import AsyncStorage from '@react-native-async-storage/async-storage';
import { ColorSchemeName } from 'react-native';
import { ScopedAsyncStorage } from '@/lib/storage';

/**
 * Theme preference type
 * - 'system': Follow system appearance settings
 * - 'light': Always use light theme
 * - 'dark': Always use dark theme
 */
export type ThemePreference = 'system' | 'light' | 'dark';

const THEME_STORAGE_KEY = '@fimfam/theme-preference';

/**
 * ThemeManager - Singleton
 * Manages theme preference storage and retrieval.
 * Provides methods to get and set user's theme preference.
 */
export class ThemeManager {
  private static instance: ThemeManager;
  private currentPreference: ThemePreference = 'system';
  private initialized = false;
  private storage: ScopedAsyncStorage | null = null;

  private constructor() {}

  /**
   * Get singleton instance
   */
  static getInstance(): ThemeManager {
    if (!ThemeManager.instance) {
      ThemeManager.instance = new ThemeManager();
    }
    return ThemeManager.instance;
  }

  /**
   * Set scoped storage and reset initialization
   * Call this when user or device changes
   */
  setStorage(storage: ScopedAsyncStorage): void {
    this.storage = storage;
    this.initialized = false;
    this.currentPreference = 'system';
  }

  /**
   * Initialize theme manager by loading stored preference
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    try {
      const storedPreference = this.storage
        ? await this.storage.getItem(THEME_STORAGE_KEY)
        : await AsyncStorage.getItem(THEME_STORAGE_KEY);
      if (storedPreference && this.isValidPreference(storedPreference)) {
        this.currentPreference = storedPreference as ThemePreference;
      }
      this.initialized = true;
    } catch (error) {
      console.error('[ThemeManager:initialize] Failed to load theme preference:', error);
      this.currentPreference = 'system';
      this.initialized = true;
    }
  }

  /**
   * Get current theme preference
   */
  getPreference(): ThemePreference {
    return this.currentPreference;
  }

  /**
   * Set theme preference and persist to storage
   */
  async setPreference(preference: ThemePreference): Promise<void> {
    if (!this.isValidPreference(preference)) {
      console.error('[ThemeManager:setPreference] Invalid preference:', preference);
      return;
    }

    try {
      this.currentPreference = preference;
      if (this.storage) {
        await this.storage.setItem(THEME_STORAGE_KEY, preference);
      } else {
        await AsyncStorage.setItem(THEME_STORAGE_KEY, preference);
      }
    } catch (error) {
      console.error('[ThemeManager:setPreference] Failed to save theme preference:', error);
      throw error;
    }
  }

  /**
   * Resolve actual color scheme based on preference and system theme
   */
  resolveColorScheme(systemColorScheme: ColorSchemeName): 'light' | 'dark' {
    if (this.currentPreference === 'system') {
      return systemColorScheme === 'dark' ? 'dark' : 'light';
    }
    return this.currentPreference;
  }

  /**
   * Get display label for theme preference
   */
  getPreferenceLabel(preference: ThemePreference): string {
    switch (preference) {
      case 'system':
        return 'System';
      case 'light':
        return 'Light';
      case 'dark':
        return 'Dark';
      default:
        return 'System';
    }
  }

  /**
   * Validate theme preference
   */
  private isValidPreference(value: string): value is ThemePreference {
    return value === 'system' || value === 'light' || value === 'dark';
  }
}
